import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, ShoppingCart, WhatsApp, Search, Email
} from '@mui/icons-material';
import {
  Button, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery
} from '@mui/material';
import SaleModal from './SaleModal';
import VerifySaleModal from './VerifySaleModal';
import { printBillPDF, shareViaWhatsApp, shareViaEmail } from './BillPdfGenerator';
import { printPackingSlip } from './PackingSlipGenerator';
import './SaleIndex.css';

const SaleIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory(); // eslint-disable-line no-unused-vars
  const { companyId, branchId, token, userId } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [selectedItem, setSelectedItem] = useState(null);

  // Verify modal states
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifySaleData, setVerifySaleData] = useState(null);
  const [verifySaleDetails, setVerifySaleDetails] = useState([]);

  // Data states
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [filterStatus, setFilterStatus] = useState('all');
  // eslint-disable-next-line no-unused-vars
  const [filterVerified, setFilterVerified] = useState('all');
  // eslint-disable-next-line no-unused-vars
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width:600px)');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [saleDetails, setSaleDetails] = useState([]);
  const [relatedItems, setRelatedItems] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Print and WhatsApp loading states
  const [printingItemId, setPrintingItemId] = useState(null);
  const [sharingItemId, setSharingItemId] = useState(null);
  const [printingPackingSlipId, setPrintingPackingSlipId] = useState(null);

  // Email share states
  const [emailingItemId, setEmailingItemId] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogData, setEmailDialogData] = useState(null); // { saleId, customerEmail, customerName, billNo }
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchSales = async () => {
    try {
      const activeResponse = await fetchData('SaleApi', `/ActiveList/${companyId}/${branchId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('SaleApi', `/InActiveList/${companyId}/${branchId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive sales:', inactiveError);
      }

      setSales([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const [staffList, setStaffList] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [staffData, setStaffData] = useState({});

  const fetchStaff = async () => {
    try {
      const response = await fetchData('StaffApi', `/StaffList/${companyId}`, token);
      setStaffList(response.response_data || response || []);
    } catch (error) { console.error('Error fetching staff:', error); setStaffList([]); }
  };


  // eslint-disable-next-line no-unused-vars
  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId || 1}/0`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      console.log('Max Bill Data:', { billNumber, billPrefix, finYear, rawData: data });

      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || [];
      let freightData = null, packingData = null;
      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing'));
      }
      setFreightPackingGST({ freight: freightData, packing: packingData });
      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Loading sales...');
    try {
      await Promise.allSettled([
        fetchSales(),
        fetchCustomers(),
        fetchItems(),
        fetchBanks(),
        fetchStaff(),
        fetchFreightAndPackingGST()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleSale = async (saleId) => {
    try {
      const response = await fetchData('SaleApi', `/${saleId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single sale:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSingleSale(itemId);

      if (freshData) {
        const saleData = freshData.Sale || freshData;
        const details = freshData.SaleDetails || [];
        const productRelationDetails = freshData.SaleProductRelationDetails || [];
        const termsDetails = freshData.SalesTermsDetails || freshData.SaleTermsDetails || [];

        console.log('SalesTermsDetails from API:', freshData.SalesTermsDetails);

        setSelectedItem(saleData);

        // Build relatedItems from SaleProductRelationDetails for edit/view mode
        if (productRelationDetails.length > 0) {
          const groupedByProduct = {};
          productRelationDetails.forEach(rel => {
            const productId = rel.ProductItemId;
            if (!groupedByProduct[productId]) {
              const productItem = items.find(i => i.Id === productId);
              const parentDetail = details.find(d => d.ItemId === productId);
              groupedByProduct[productId] = {
                productId: productId,
                productName: productItem?.Name || '',
                productQuantity: parseFloat(parentDetail?.Quantity) || 0,
                items: []
              };
            }
            const componentItem = items.find(i => i.Id === rel.ItemId);
            const productQuantity = groupedByProduct[productId].productQuantity || 1;
            groupedByProduct[productId].items.push({
              ...rel,
              ItemId: rel.ItemId,
              ProductItemId: rel.ProductItemId,
              ProductChildName: componentItem?.Name || '',
              Quantity: parseFloat(rel.Quantity) || 0,
              PrevQuantity: parseFloat(rel.PrevQuantity) || 0,
              OriginalQuantity: productQuantity > 0 ? (parseFloat(rel.Quantity) || 0) / productQuantity : (parseFloat(rel.Quantity) || 0),
              ProductQuantity: productQuantity,
              BalanceStock: parseFloat(rel.BalanceStock) || 0,
              Rate: parseFloat(rel.Rate) || componentItem?.SaleRate || 0,
              SaleRate: componentItem?.SaleRate || 0
            });
          });
          setRelatedItems(Object.values(groupedByProduct));
        } else {
          setRelatedItems([]);
        }

        setFormData({
          Id: saleData.Id,
          CompanyId: saleData.CompanyId || companyId,
          BranchId: saleData.BranchId || branchId,
          MasterId: saleData.MasterId,
          CustomerName: saleData.CustomerName || '',
          VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
          SalesManId: saleData.SalesManId || null,
          StateId: saleData.StateId || null,
          CurrencyTypeId: saleData.CurrencyTypeId || null,
          CurrencyName: saleData.CurrencyName || '',
          BillNumber: saleData.BillNumber,
          BillPrefix: saleData.BillPrefix || '',
          FinYear: saleData.FinYear || '',
          BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: saleData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          DueDate: saleData.DueDate?.split('T')[0] || '',
          CreditDays: saleData.CreditDays ?? 0,
          GstCalculationType: saleData.GstCalculationType || 1,
          GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
          Amount: saleData.Amount || 0,
          DiscountAmount: saleData.DiscountAmount || 0,
          TaxableAmount: saleData.TaxableAmount || 0,
          VATAmount: saleData.VATAmount || 0,
          FreightAmount: saleData.FreightAmount || 0,
          FreightVATRate: saleData.FreightVATRate || 0,
          FreightVATAmount: saleData.FreightVATAmount || 0,
          PackingAmount: saleData.PackingAmount || 0,
          PackingVATRate: saleData.PackingVATRate || 0,
          PackingVATAmount: saleData.PackingVATAmount || 0,
          RoundOff: saleData.RoundOff || 0,
          TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
          GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
          CashAmount: saleData.CashAmount || 0,
          UPIAmount: saleData.UPIAmount || 0,
          CardAmount: saleData.CardAmount || 0,
          AdvanceAmount: saleData.AdvanceAmount || 0,
          BankId: saleData.BankId || null,
          PaidAmount: saleData.PaidAmount || 0,
          BalanceAmount: saleData.BalanceAmount || 0,
          Remarks: saleData.Remarks || '',
          // Shipping & Vehicle Details
          DispatchFrom: saleData.DispatchFrom || '',
          DispatchAddress1: saleData.DispatchAddress1 || '',
          DispatchAddress2: saleData.DispatchAddress2 || '',
          DispatchLocation: saleData.DispatchLocation || '',
          DispatchPincode: saleData.DispatchPincode || '',
          ShipTo: saleData.ShipTo || '',
          ShipGSTNo: saleData.ShipGSTNo || '',
          ShipToAddress1: saleData.ShipToAddress1 || '',
          ShipToAddress2: saleData.ShipToAddress2 || '',
          ShipToLocation: saleData.ShipToLocation || '',
          ShipToPincode: saleData.ShipToPincode || '',
          VehicleNumber: saleData.VehicleNumber || '',
          TransporterName: saleData.TransporterName || '',
          TransporterVAT: saleData.TransporterVAT || '',
          PaymentTerms: saleData.PaymentTerms || '',
          TermsofDelivery: saleData.TermsofDelivery || '',
          ReferenceNumber: saleData.ReferenceNumber || '',
          Distance: saleData.Distance || 0,
          IsVerified: saleData.IsVerified || false,
          IsActive: saleData.IsActive ?? true,
          CreatedBy: saleData.CreatedBy,
          CreatedDate: saleData.CreatedDate,
          CreatedByName: saleData.CreatedByName,
          ModifiedBy: saleData.ModifiedBy,
          ModifiedDate: saleData.ModifiedDate,
          ModifiedByName: saleData.ModifiedByName,
          // Terms & Conditions
          SelectedTerms: termsDetails.map(t => t.TermsAndConditionId || t.Id),
          SelectedTermsData: termsDetails.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || t.TermsAndCondition?.Name || '' })),
          TermsAndConditions: termsDetails.map(t => t.Name || t.TermsAndCondition?.Name || '').filter(n => n).join('\n')
        });

        console.log('Loaded Sale Data:', {
          DispatchFrom: saleData.DispatchFrom,
          ShipTo: saleData.ShipTo,
          ShipGSTNo: saleData.ShipGSTNo,
          ShipToAddress1: saleData.ShipToAddress1
        });
        setSaleDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          VATRate: d.VATRate || d.GSTRate || 0,
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          PrevQuantity:d.PrevQuantity||0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          VATAmount: d.VATAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate
        })));
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const gstData = await fetchFreightAndPackingGST();

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: '',
        BillNumber: '',
        BillPrefix: '',
        FinYear: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        DueDate: new Date().toISOString().split('T')[0],
        CreditDays: 0,
        CurrencyTypeId: null,
        CurrencyName: '',
        GstCalculationType: 1,
        GstCalculationTypeName: 'Excluding Tax',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        VATAmount: 0,
        FreightAmount: 0,
        FreightVATRate: parseFloat(gstData.freight?.VATRate) || 0,
        FreightVATAmount: 0,
        PackingAmount: 0,
        PackingVATRate: parseFloat(gstData.packing?.VATRate) || 0,
        PackingVATAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        GrandTotal: 0,
        CashAmount: 0,
        UPIAmount: 0,
        CardAmount: 0,
        AdvanceAmount: 0,
        BankId: null,
        BalanceAmount: 0,
        Remarks: '',
        // Shipping & Vehicle Details
        DispatchFrom: '',
        DispatchAddress1: '',
        DispatchAddress2: '',
        DispatchLocation: '',
        DispatchPincode: '',
        ShipTo: '',
        ShipGSTNo: '',
        ShipToAddress1: '',
        ShipToAddress2: '',
        ShipToLocation: '',
        ShipToPincode: '',
        VehicleNumber: '',
        TransporterName: '',
        TransporterVAT: '',
        PaymentTerms: '',
        TermsofDelivery: '',
        ReferenceNumber: '',
        Distance: 0,
        IsVerified: false,
        IsActive: true
      });
      setSaleDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setSaleDetails([]);
    window.location.reload();
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} sale...`);

      // Prepare ProductComponents from relatedItems
      const productComponents = [];
      if (relatedItems && relatedItems.length > 0) {
        relatedItems.forEach(productGroup => {
          if (productGroup.items && productGroup.items.length > 0) {
            productGroup.items.forEach(relatedItem => {
              productComponents.push({
                Id: 0, // Always 0 for new entries
                SaleId: modalAction === 'edit' ? formData.Id : 0,
                ProductItemId: productGroup.productId,
                ItemId: relatedItem.ItemId || relatedItem.Id,
                Quantity: parseFloat(relatedItem.Quantity) || 0,
                PrevQuantity: parseFloat(relatedItem.PrevQuantity) || 0,
                Rate: parseFloat(relatedItem.Rate) || 0,
                Amount: parseFloat(relatedItem.Amount) || 0,
                IsActive: true
              });
            });
          }
        });
      }

      const dataToSend = {
        Sale: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          MasterId: parseInt(formData.MasterId) || 0,
          CustomerName: formData.CustomerName || '',
          GSTNumber: formData.VATNumber || formData.GSTNumber || '',
          SalesManId: formData.SalesManId || null,
          StateId: formData.StateId || null,
          CurrencyTypeId: formData.CurrencyTypeId || null,
          CurrencyName: formData.CurrencyName || '',
          BillNumber: formData.BillNumber || '',
          BillPrefix: formData.BillPrefix || '',
          FinYear: formData.FinYear || '',
          BillDate: formData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: formData.ClearingDate || new Date().toISOString().split('T')[0],
          DueDate: formData.DueDate || null,
          CreditDays: parseInt(formData.CreditDays) || 0,
          GstCalculationType: formData.GstCalculationType || 1,
          GstCalculationTypeName: formData.GstCalculationTypeName || 'Excluding Tax',
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          VATAmount: parseFloat(formData.VATAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightVATRate: parseFloat(formData.FreightVATRate) || 0,
          FreightVATAmount: parseFloat(formData.FreightVATAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingVATRate: parseFloat(formData.PackingVATRate) || 0,
          PackingVATAmount: parseFloat(formData.PackingVATAmount) || 0,
          RoundOff: parseFloat(formData.RoundOff) || 0,
          TotalAmount: parseFloat(formData.GrandTotal) || parseFloat(formData.TotalAmount) || 0,
          GrandTotal: parseFloat(formData.GrandTotal) || parseFloat(formData.TotalAmount) || 0,
          CashAmount: parseFloat(formData.CashAmount) || 0,
          UPIAmount: parseFloat(formData.UPIAmount) || 0,
          CardAmount: parseFloat(formData.CardAmount) || 0,
          AdvanceAmount: parseFloat(formData.AdvanceAmount) || 0,
          BankId: formData.BankId || null,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Remarks: formData.Remarks || '',
          // Shipping & Vehicle Details
          DispatchFrom: formData.DispatchFrom || '',
          DispatchAddress1: formData.DispatchAddress1 || '',
          DispatchAddress2: formData.DispatchAddress2 || '',
          DispatchLocation: formData.DispatchLocation || '',
          DispatchPincode: formData.DispatchPincode || '',
          ShipTo: formData.ShipTo || '',
          ShipGSTNo: formData.ShipGSTNo || '',
          ShipToAddress1: formData.ShipToAddress1 || '',
          ShipToAddress2: formData.ShipToAddress2 || '',
          ShipToLocation: formData.ShipToLocation || '',
          ShipToPincode: formData.ShipToPincode || '',
          VehicleNumber: formData.VehicleNumber || '',
          TransporterName: formData.TransporterName || '',
          TransporterVAT: formData.TransporterVAT || '',
          Distance: parseInt(formData.Distance) || 0,
          PaymentTerms: formData.PaymentTerms || '',
          TermsofDelivery: formData.TermsofDelivery || '',
          ReferenceNumber: formData.ReferenceNumber || '',

          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? (formData.SalesManId || userId) : formData.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : formData.CreatedDate, // Preserve original created date
          ModifiedBy: modalAction === 'edit' ? (formData.SalesManId || userId) : null
        },
        SaleDetails: saleDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          SaleId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          VATRate: parseFloat(detail.VATRate || detail.GSTRate) || 0,
          Quantity: parseFloat(detail.Quantity) || 0,
          PrevQuantity: parseFloat(detail.PrevQuantity) || 0,
          Rate: parseFloat(detail.Rate) || 0,
          Amount: parseFloat(detail.Amount) || 0,
          DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
          TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
          VATAmount: parseFloat(detail.VATAmount) || 0,
          TotalAmount: parseFloat(detail.TotalAmount) || 0,
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? (formData.SalesManId || userId) : detail.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : detail.CreatedDate, // Preserve original created date
          ModifiedBy: modalAction === 'edit' ? (formData.SalesManId || userId) : null
        })),
        SaleProductRelationDetails: productComponents,
        SalesTermsDetails: (formData.SelectedTermsData && formData.SelectedTermsData.length > 0) 
          ? formData.SelectedTermsData.map(term => ({
              Id: 0,
              CompanyId: companyId,
              TermsAndConditionId: term.Id,
              SaleId: modalAction === 'edit' ? (formData.Id || 0) : 0,
              Name: term.Name || '',
              IsActive: true,
              CreatedBy: modalAction === 'create' ? (formData.SalesManId || userId) : (formData.SalesManId || userId),
              ModifiedBy: modalAction === 'edit' ? (formData.SalesManId || userId) : null,
              ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
            }))
          : []
      };

      if (modalAction === 'create') {
        await postData('SaleApi', '', dataToSend, token);
        setSuccess('Sale created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Sale ID is missing');
        await updateData('SaleApi', formData.Id, dataToSend, token);
        setSuccess('Sale updated successfully!');
      }

      handleCloseModal();
      await fetchSales();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} sale. Please try again.`);
      setLoading(false);
    }
  };

  // Print handler
  const handlePrint = async (saleId) => {
    setPrintingItemId(saleId);
    try {
      // Fetch sale details using the same method as fetchSingleSale
      const freshData = await fetchSingleSale(saleId);

      if (!freshData) {
        setError('Failed to fetch sale details.');
        setPrintingItemId(null);
        return;
      }

      const saleData = freshData.Sale || freshData;
      const details = freshData.SaleDetails || [];
      const salesTerms = freshData.SalesTermsDetails || freshData.SaleTermsDetails || [];

      console.log('Print - saleData.GSTNumber:', saleData.GSTNumber, 'saleData.VATNumber:', saleData.VATNumber, 'saleData.ReferenceNumber:', saleData.ReferenceNumber);

      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode === '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName ,
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || 'Dubai'}, UAE`.trim(),
        trn: branchData.VATNumber,
        phone: branchData.ContactNumber1 + (branchData.ContactNumber2 ? ',' + branchData.ContactNumber2 : '') + (branchData.ContactNumber3 ? ',' + branchData.ContactNumber3 : ''),
        email: branchData.Email ,
        vatNumber: branchData.VATNumber 
      };

      const saleDataForPDF = {
        ...saleData,
        saleDetails: details,
        CustomerName: saleData.CustomerName || '',
        VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
        ReferenceNumber : saleData.ReferenceNumber ||  '',
        SalesmanName: saleData.SalesmanName || '',
        BillPrefix: saleData.BillPrefix || '',
        FinYear: saleData.FinYear || '',
        BillNumber: saleData.BillNumber,
        BillDate: saleData.BillDate,
        TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
        TaxableAmount: saleData.TaxableAmount || 0,
        VATAmount: saleData.VATAmount || 0,
        FreightAmount: saleData.FreightAmount || 0,
        PackingAmount: saleData.PackingAmount || 0,
        CashAmount: saleData.CashAmount || 0,
        UPIAmount: saleData.UPIAmount || 0,
        CardAmount: saleData.CardAmount || 0,
        AdvanceAmount: saleData.AdvanceAmount || 0,
        BalanceAmount: saleData.BalanceAmount || 0,
        ShipTo: saleData.ShipTo || '',
        ShipGSTNo: saleData.ShipGSTNo || '',
        ShipToAddress1: saleData.ShipToAddress1 || '',
        ShipToAddress2: saleData.ShipToAddress2 || '',
        SelectedTermsData: salesTerms.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || '' }))
      };

      await printBillPDF(saleDataForPDF, companyDetails, items, banks);
      setSuccess('Print dialog opened!');
    } catch (error) {
      console.error('Error printing:', error);
      setError('Failed to print invoice.');
    } finally {
      setPrintingItemId(null);
    }
  };

  // WhatsApp share handler
  const handleWhatsAppShare = async (saleId) => {
    setSharingItemId(saleId);
    try {
      // Fetch sale details using the same method as fetchSingleSale
      const freshData = await fetchSingleSale(saleId);

      if (!freshData) {
        setError('Failed to fetch sale details.');
        setSharingItemId(null);
        return;
      }

      const saleData = freshData.Sale || freshData;
      const details = freshData.SaleDetails || [];
      const salesTerms = freshData.SalesTermsDetails || freshData.SaleTermsDetails || [];

      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode === '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName ,
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || ''}, `.trim(),
        trn: branchData.VATNumber,
        phone: branchData.ContactNumber1 + (branchData.ContactNumber2 ? ',' + branchData.ContactNumber2 : '') + (branchData.ContactNumber3 ? ',' + branchData.ContactNumber3 : ''),
        email: branchData.Email,
        vatNumber: branchData.VATNumber 
      };

      const saleDataForPDF = {
        ...saleData,
        saleDetails: details,
        CustomerName: saleData.CustomerName || '',
        VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
        SalesmanName: saleData.SalesmanName || '',
        BillPrefix: saleData.BillPrefix || '',
        FinYear: saleData.FinYear || '',
        BillNumber: saleData.BillNumber,
        BillDate: saleData.BillDate,
        TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
        TaxableAmount: saleData.TaxableAmount || 0,
        VATAmount: saleData.VATAmount || 0,
        FreightAmount: saleData.FreightAmount || 0,
        PackingAmount: saleData.PackingAmount || 0,
        CashAmount: saleData.CashAmount || 0,
        UPIAmount: saleData.UPIAmount || 0,
        CardAmount: saleData.CardAmount || 0,
        AdvanceAmount: saleData.AdvanceAmount || 0,
        BalanceAmount: saleData.BalanceAmount || 0,
        ShipTo: saleData.ShipTo || '',
        ShipGSTNo: saleData.ShipGSTNo || '',
        ShipToAddress1: saleData.ShipToAddress1 || '',
        ShipToAddress2: saleData.ShipToAddress2 || '',
        SelectedTermsData: salesTerms.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || '' }))
      };

      const customerPhone = saleData.CustomerPhone || '';
      const result = await shareViaWhatsApp(saleDataForPDF, companyDetails, items, customerPhone, banks);

      if (result.success) {
        if (result.method === 'fallback') {
          setSuccess(result.message);
        } else {
          setSuccess('Shared via WhatsApp successfully!');
        }
      } else {
        setError(result.error || 'Failed to share via WhatsApp');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setError('Failed to share invoice.');
    } finally {
      setSharingItemId(null);
    }
  };

  // Email share — open dialog to confirm/enter email address first
  const handleEmailShareOpen = async (saleId) => {
    setEmailingItemId(saleId);
    try {
      const freshData = await fetchSingleSale(saleId);
      if (!freshData) {
        setError('Failed to fetch sale details.');
        setEmailingItemId(null);
        return;
      }
      const saleData = freshData.Sale || freshData;

      // Try to get customer email from customers list
      const customer = customers.find(c => c.Id === saleData.ClientId || c.Id === saleData.CustomerId);
      const customerEmail = customer?.Email || saleData.CustomerEmail || '';
      const billNo = saleData.BillPrefix && saleData.FinYear
        ? `${saleData.BillPrefix}/${saleData.BillNumber}/${saleData.FinYear}`
        : saleData.BillNumber || '';

      setEmailDialogData({ saleId, customerEmail, customerName: saleData.CustomerName || '', billNo });
      setEmailInput(customerEmail);
      setEmailDialogOpen(true);
    } catch (error) {
      console.error('Error opening email dialog:', error);
      setError('Failed to load sale details for email.');
    } finally {
      setEmailingItemId(null);
    }
  };

  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setEmailDialogData(null);
    setEmailInput('');
    setEmailSending(false);
  };

  // Actually send/share the email after user confirms the address
  const handleEmailSend = async () => {
    if (!emailDialogData) return;
    setEmailSending(true);
    try {
      const { saleId } = emailDialogData;
      const freshData = await fetchSingleSale(saleId);
      if (!freshData) {
        setError('Failed to fetch sale details.');
        handleEmailDialogClose();
        return;
      }
      const saleData = freshData.Sale || freshData;
      const details = freshData.SaleDetails || [];
      const salesTerms = freshData.SalesTermsDetails || freshData.SaleTermsDetails || [];

      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode === '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName,
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || ''}`.trim(),
        trn: branchData.VATNumber,
        phone: branchData.ContactNumber1 + (branchData.ContactNumber2 ? ',' + branchData.ContactNumber2 : '') + (branchData.ContactNumber3 ? ',' + branchData.ContactNumber3 : ''),
        email: branchData.Email,
        vatNumber: branchData.VATNumber,
      };

      const saleDataForPDF = {
        ...saleData,
        saleDetails: details,
        CustomerName: saleData.CustomerName || '',
        VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
        SalesmanName: saleData.SalesmanName || '',
        BillPrefix: saleData.BillPrefix || '',
        FinYear: saleData.FinYear || '',
        BillNumber: saleData.BillNumber,
        BillDate: saleData.BillDate,
        TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
        TaxableAmount: saleData.TaxableAmount || 0,
        VATAmount: saleData.VATAmount || 0,
        FreightAmount: saleData.FreightAmount || 0,
        PackingAmount: saleData.PackingAmount || 0,
        CashAmount: saleData.CashAmount || 0,
        UPIAmount: saleData.UPIAmount || 0,
        CardAmount: saleData.CardAmount || 0,
        AdvanceAmount: saleData.AdvanceAmount || 0,
        BalanceAmount: saleData.BalanceAmount || 0,
        ShipTo: saleData.ShipTo || '',
        ShipGSTNo: saleData.ShipGSTNo || '',
        ShipToAddress1: saleData.ShipToAddress1 || '',
        ShipToAddress2: saleData.ShipToAddress2 || '',
        SelectedTermsData: salesTerms.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || '' }))
      };

      const result = await shareViaEmail(saleDataForPDF, companyDetails, items, emailInput.trim(), apiConfig.emailjs, banks);
      handleEmailDialogClose();

      if (result.success) {
        if (result.method === 'fallback') {
          setSuccess(result.message || 'PDF downloaded. Attach it in the email that opened.');
        } else {
          setSuccess('Invoice sent via email successfully!');
        }
      } else {
        setError(result.error || 'Failed to share invoice via email.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Failed to share invoice via email.');
      handleEmailDialogClose();
    }
  };

  // Packing Slip print handler
  const handlePrintPackingSlip = async (saleId) => {
    setPrintingPackingSlipId(saleId);
    try {
      // Fetch sale details
      const freshData = await fetchSingleSale(saleId);

      if (!freshData) {
        setError('Failed to fetch sale details.');
        setPrintingPackingSlipId(null);
        return;
      }

      const saleData = freshData.Sale || freshData;
      const details = freshData.SaleDetails || [];
      const productRelationDetails = freshData.SaleProductRelationDetails || [];

      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode === '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName ,
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || ''}, `.trim(),
        trn: branchData.VATNumber ,
        phone: branchData.ContactNumber1 + (branchData.ContactNumber2 ? ',' + branchData.ContactNumber2 : '') + (branchData.ContactNumber3 ? ',' + branchData.ContactNumber3 : ''),
        email: branchData.Email ,
        vatNumber: branchData.VATNumber
      };

      const saleDataForPDF = {
        ...saleData,
        saleDetails: details,
        CustomerName: saleData.CustomerName || '',
        VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
        SalesmanName: saleData.SalesmanName || '',
        BillPrefix: saleData.BillPrefix || '',
        FinYear: saleData.FinYear || '',
        BillNumber: saleData.BillNumber,
        BillDate: saleData.BillDate,
        ShipTo: saleData.ShipTo || '',
        ShipGSTNo: saleData.ShipGSTNo || '',
        ShipToAddress1: saleData.ShipToAddress1 || '',
        ShipToAddress2: saleData.ShipToAddress2 || ''
      };

      await printPackingSlip(saleDataForPDF, companyDetails, items, productRelationDetails);
      setSuccess('Packing slip print dialog opened!');
    } catch (error) {
      console.error('Error printing packing slip:', error);
      setError('Failed to print packing slip.');
    } finally {
      setPrintingPackingSlipId(null);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('SaleApi', deleteItemId, token);
      setSuccess('Sale deleted successfully!');
      await fetchSales();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete sale.');
    }
    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    try {
      await activateData('SaleApi', activateItemId, token);
      setSuccess('Sale activated successfully!');
      await fetchSales();
    } catch (error) {
      console.error('Error activating:', error);
      setError('Failed to activate sale.');
    }
    setLoading(false);
  };


  // Verify handlers
  const handleOpenVerifyModal = async (item) => {
    setModalLoading(true);
    const freshData = await fetchSingleSale(item.Id);

    if (freshData) {
      const saleData = freshData.Sale || freshData;
      const details = freshData.SaleDetails || [];

      setVerifySaleData(saleData);
      setVerifySaleDetails(details);
    } else {
      setVerifySaleData(item);
      setVerifySaleDetails([]);
    }
    setModalLoading(false);
    setVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setVerifySaleData(null);
    setVerifySaleDetails([]);
  };

  const handleVerify = async (saleId, updatedDetails) => {
    setLoading(true);
    setLoadingMessage('Updating sale...');

    try {
      const dataToSend = {
        Sale: {
          ...verifySaleData,
          ModifiedBy: userId
        },
        SaleDetails: updatedDetails.map(d => ({
          ...d,
          ModifiedBy: userId
        }))
      };

      await updateData('SaleApi', saleId, dataToSend, token);
      setSuccess('Sale updated successfully!');
      await fetchSales();
    } catch (error) {
      console.error('Error updating sale:', error);
      setError('Failed to update sale.');
    }
    setLoading(false);
  };

  const handleActualVerify = async (saleId, updatedDetails) => {
    setLoading(true);
    setLoadingMessage('Verifying sale...');

    try {
      await VerifyData('SaleApi', saleId, token);
      setSuccess('Sale verified successfully!');
      await fetchSales();
      handleCloseVerifyModal();
    } catch (error) {
      console.error('Error verifying sale:', error);
      setError('Failed to verify sale.');
    }
    setLoading(false);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];

    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item => {
        const fullBillNo = item.BillPrefix && item.FinYear
          ? `${item.BillPrefix}/${item.BillNumber}/${item.FinYear}`
          : item.BillNumber?.toString() || '';

        return fullBillNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customers.find(c => c.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  // eslint-disable-next-line no-unused-vars
  const handleRefresh = () => fetchAllData();
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return ` ${(amount || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(sales);


  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {loading && <LoadingOverlay message={loadingMessage} />}

        {/* Header Section with Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShoppingCart sx={{ fontSize: 24, color: '#0f3460' }} />
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Romans',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Sale Management</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
        <div style={{
          marginBottom: '12px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-end',
            minWidth: 'fit-content',
            paddingBottom: '4px'
          }}>
            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={handleFromDateChange}
                sx={{
                  width: isMobile ? '120px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={handleToDateChange}
                sx={{
                  width: isMobile ? '120px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* Search - Icon on mobile, always visible on desktop */}
            {isMobile ? (
              searchExpanded ? (
                <TextField
                  size="small"
                  placeholder="Search by Bill No, Customer..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  autoFocus
                  onBlur={() => {
                    if (!searchTerm) {
                      setSearchExpanded(false);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: '250px',
                    width: '350px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#0f3460',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0f3460',
                      },
                    },
                  }}
                />
              ) : (
                <Tooltip title="Search" arrow>
                  <Button
                    onClick={() => setSearchExpanded(true)}
                    style={{
                      minWidth: '32px',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                  </Button>
                </Tooltip>
              )
            ) : (
              <TextField
                size="small"
                placeholder="Search by Bill No, Customer..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: '250px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '12px',
                    height: '32px',
                    '&:hover fieldset': {
                      borderColor: '#0f3460',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0f3460',
                    },
                  },
                }}
              />
            )}

            {/* Create Sale Button */}
            {canCreateTransaction && (
              <Tooltip title="Create a new sale" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleOpenModal('create')}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : '100px',
                    width: 'auto',
                    marginLeft: 'auto'
                  }}
                >
                  <Add style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <Table sx={{ minWidth: 650 }} aria-label="sale table">
              <TableHead>
                <TableRow style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  height: '27px'
                }}>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'center',
                    height: '27px'
                  }}>
                    Sl No.
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill No.
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Date
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Customer
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Total Amount
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Cash
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Card
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    UPI
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Balance
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id || index} style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>
                        {startIndex + index + 1}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ShoppingCart sx={{ fontSize: 18, color: '#0f3460' }} />
                          {item.BillPrefix && item.FinYear
                            ? `${item.BillPrefix}/${item.BillNumber}/${item.FinYear}`
                            : item.BillNumber || '-'}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.CustomerName || customers.find(c => c.MasterId === item.MasterId)?.Name || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>
                        {formatCurrency(item.TotalAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#059669', fontWeight: 600, textAlign: 'right' }}>
                        {formatCurrency(item.CashAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#1e40af', fontWeight: 600, textAlign: 'right' }}>
                        {formatCurrency(item.CardAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#7c3aed', fontWeight: 600, textAlign: 'right' }}>
                        {formatCurrency(item.UPIAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#dc2626', fontWeight: 600, textAlign: 'right' }}>
                        {formatCurrency(item.BalanceAmount)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Chip
                          label={item.IsActive ? 'Active' : 'Inactive'}
                          color={item.IsActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Tooltip title="Print Invoice" arrow>
                            <Button
                              size="small"
                              onClick={() => handlePrint(item.Id)}
                              disabled={printingItemId === item.Id}
                              style={{
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                backgroundColor: 'white',
                                color: 'white',
                                padding: '2px',
                                boxShadow: '0 1px 4px rgba(37, 99, 235, 0.3)',
                                border: 'none',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}
                            >
                              {printingItemId === item.Id ? '⏳' : '🖨️'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Print Packing Slip" arrow>
                            <Button
                              size="small"
                              onClick={() => handlePrintPackingSlip(item.Id)}
                              disabled={printingPackingSlipId === item.Id}
                              style={{
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                backgroundColor: 'white',
                                color: 'white',
                                padding: '2px',
                                boxShadow: '0 1px 4px rgba(34, 197, 94, 0.3)',
                                border: 'none',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}
                            >
                              {printingPackingSlipId === item.Id ? '⏳' : '📦'}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Share WhatsApp" arrow>
                            <Button
                              size="small"
                              onClick={() => handleWhatsAppShare(item.Id)}
                              disabled={sharingItemId === item.Id}
                              style={{
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                backgroundColor: '#25D366',
                                color: 'white',
                                padding: '2px',
                                boxShadow: '0 1px 4px rgba(37, 211, 102, 0.3)',
                                border: 'none',
                                fontSize: '10px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {sharingItemId === item.Id ? (
                                <span style={{ fontSize: 14 }}>⏳</span>
                              ) : (
                                <WhatsApp sx={{ fontSize: 14, color: '#ffffff' }} />
                              )}
                            </Button>
                          </Tooltip>
                          {/* <Tooltip title="Send via Email" arrow>
                            <Button
                              size="small"
                              onClick={() => handleEmailShareOpen(item.Id)}
                              disabled={emailingItemId === item.Id}
                              style={{
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                backgroundColor: '#EA4335',
                                color: 'white',
                                padding: '2px',
                                boxShadow: '0 1px 4px rgba(234, 67, 53, 0.3)',
                                border: 'none',
                                fontSize: '10px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {emailingItemId === item.Id ? (
                                <span style={{ fontSize: 14 }}>⏳</span>
                              ) : (
                                <Email sx={{ fontSize: 14, color: '#ffffff' }} />
                              )}
                            </Button>
                          </Tooltip> */}
                          <Tooltip title="View Details" arrow>
                            <Button
                              size="small"
                              onClick={() => handleOpenModal('view', item.Id)}
                              style={{
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                color: 'white',
                                padding: '2px',
                                boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                border: 'none',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}
                            >
                              👁️
                            </Button>
                          </Tooltip>
                          {item.IsActive && !item.IsVerified && canEditTransaction && (
                            <Tooltip title="Edit Sale" arrow>
                              <Button
                                size="small"
                                onClick={() => handleOpenModal('edit', item.Id)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✏️
                              </Button>
                            </Tooltip>
                          )}
                          {item.IsActive && !item.IsVerified && canDeleteTransaction && (
                            <Tooltip title="Delete Sale" arrow>
                              <Button
                                size="small"
                                onClick={() => handleOpenDeleteModal(item.Id, item.BillNumber)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                🗑️
                              </Button>
                            </Tooltip>
                          )}
                          {!item.IsActive && canEditTransaction && (
                            <Tooltip title="Activate Sale" arrow>
                              <Button
                                size="small"
                                onClick={() => handleOpenActivateModal(item.Id, item.BillNumber)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  backgroundColor: '#059669',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(5, 150, 105, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✓
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      No sales found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination Section - Mobile Optimized */}
        {totalCount > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              padding: isMobile ? '8px' : '8px 12px',
              background: '#f8f9fa',
              borderRadius: '8px',
              gap: isMobile ? '8px' : '0'
            }}>
              {/* Info and Rows per page - Combined on mobile */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <div style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                  {isMobile ? `${startIndex + 1}-${Math.min(startIndex + rowsPerPage, totalCount)} of ${totalCount}` : `Showing ${totalCount > 0 ? startIndex + 1 : 0} to ${Math.min(startIndex + rowsPerPage, totalCount)} of ${totalCount} entries`}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b' }}>Rows:</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    style={{
                      padding: isMobile ? '3px 6px' : '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      fontSize: isMobile ? '11px' : '13px',
                      cursor: 'pointer',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Pagination Controls */}
              <div style={{
                display: 'flex',
                gap: isMobile ? '3px' : '4px',
                justifyContent: isMobile ? 'center' : 'flex-end',
                flexWrap: 'wrap'
              }}>
                <Button
                  size="small"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: isMobile ? '32px' : '60px',
                    fontSize: isMobile ? '11px' : '12px',
                    textTransform: 'none',
                    bgcolor: currentPage === 1 ? '#f1f5f9' : 'white',
                    color: currentPage === 1 ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    padding: isMobile ? '4px 6px' : '4px 12px',
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  {isMobile ? '«' : 'First'}
                </Button>
                <Button
                  size="small"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: isMobile ? '32px' : '60px',
                    fontSize: isMobile ? '11px' : '12px',
                    textTransform: 'none',
                    bgcolor: currentPage === 1 ? '#f1f5f9' : 'white',
                    color: currentPage === 1 ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    padding: isMobile ? '4px 6px' : '4px 12px',
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  {isMobile ? '‹' : 'Prev'}
                </Button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  // On mobile, show fewer page numbers
                  if (isMobile) {
                    if (page === currentPage || page === 1 || page === totalPages) {
                      return (
                        <Button
                          key={page}
                          size="small"
                          onClick={() => handlePageChange(page)}
                          sx={{
                            minWidth: '32px',
                            fontSize: '11px',
                            bgcolor: currentPage === page ? '#0f3460' : 'white',
                            color: currentPage === page ? 'white' : '#475569',
                            border: '1px solid #e2e8f0',
                            padding: '4px 6px',
                            fontWeight: currentPage === page ? 600 : 400,
                            '&:hover': { bgcolor: currentPage === page ? '#5568d3' : '#f8fafc' }
                          }}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 1 || page === currentPage + 1) {
                      return <span key={page} style={{ padding: '0 2px', color: '#94a3b8', fontSize: '11px' }}>...</span>;
                    }
                  } else {
                    // Desktop view - show more pages
                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                      return (
                        <Button
                          key={page}
                          size="small"
                          onClick={() => handlePageChange(page)}
                          sx={{
                            minWidth: '36px',
                            fontSize: '12px',
                            bgcolor: currentPage === page ? '#0f3460' : 'white',
                            color: currentPage === page ? 'white' : '#475569',
                            border: '1px solid #e2e8f0',
                            '&:hover': { bgcolor: currentPage === page ? '#5568d3' : '#f8fafc' }
                          }}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>;
                    }
                  }
                  return null;
                })}
                <Button
                  size="small"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  sx={{
                    minWidth: isMobile ? '32px' : '60px',
                    fontSize: isMobile ? '11px' : '12px',
                    textTransform: 'none',
                    bgcolor: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : 'white',
                    color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    padding: isMobile ? '4px 6px' : '4px 12px',
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  {isMobile ? '›' : 'Next'}
                </Button>
                <Button
                  size="small"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  sx={{
                    minWidth: isMobile ? '32px' : '60px',
                    fontSize: isMobile ? '11px' : '12px',
                    textTransform: 'none',
                    bgcolor: (currentPage === totalPages || totalPages === 0) ? '#f1f5f9' : 'white',
                    color: (currentPage === totalPages || totalPages === 0) ? '#94a3b8' : '#475569',
                    border: '1px solid #e2e8f0',
                    padding: isMobile ? '4px 6px' : '4px 12px',
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  {isMobile ? '»' : 'Last'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sale Modal */}
      <SaleModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        formData={formData}
        setFormData={setFormData}
        saleDetails={saleDetails}
        setSaleDetails={setSaleDetails}
        relatedItems={relatedItems}
        setRelatedItems={setRelatedItems}
        onSave={handleSave}
        onVerify={handleOpenVerifyModal}
        customers={customers}
        items={items}
        banks={banks}
        staffList={staffList}
        loading={modalLoading}
        freightPackingGST={freightPackingGST}
        companyId={companyId}
        branchId={branchId}
        token={token}
      />

      {/* Verify Sale Modal */}
      <VerifySaleModal
        open={verifyModalOpen}
        onClose={handleCloseVerifyModal}
        formData={verifySaleData}
        saleDetails={verifySaleDetails}
        onVerify={handleVerify}
        onActualVerify={handleActualVerify}
        customers={customers}
        items={items}
        loading={modalLoading}
      />

      {/* Email Share Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px' } }}
      >
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
          <Email style={{ color: '#EA4335', fontSize: '22px' }} />
          Send Invoice via Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: '12px', color: '#6b7280' }}>
            {emailDialogData?.customerName
              ? `Sending invoice ${emailDialogData.billNo} to ${emailDialogData.customerName}`
              : `Sending invoice ${emailDialogData?.billNo || ''}`}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Customer Email Address"
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="customer@example.com"
            disabled={emailSending}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email style={{ fontSize: '18px', color: '#EA4335' }} />
                </InputAdornment>
              ),
            }}
            helperText="The invoice PDF will be sent directly to this email address."
          />
        </DialogContent>
        <DialogActions style={{ padding: '12px 16px', gap: '8px' }}>
          <Button
            onClick={handleEmailDialogClose}
            disabled={emailSending}
            style={{ color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEmailSend}
            disabled={emailSending || !emailInput.trim()}
            variant="contained"
            startIcon={emailSending ? null : <Email />}
            style={{
              backgroundColor: '#EA4335',
              color: 'white',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: '600',
            }}
          >
            {emailSending ? 'Preparing...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete sale <strong>{deleteItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal}>
        <DialogTitle>Confirm Activate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to activate sale <strong>{activateItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SaleIndex;
